import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import HeroSection from './components/HeroSection/HeroSection';
import EarnMoneySection from './components/EarnMoneySection/EarnMoneySection';
import SubscriptionPlans from './components/SubscriptionPlans/SubscriptionPlans';
import PaymentConfirmation from './components/PaymentConfirmation/PaymentConfirmation';
import TermsConditions from './components/TermsConditions/TermsConditions';
import PaymentModal from './components/PaymentModal/PaymentModal';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Login from './components/Auth/Login';
import SignUp from './components/Auth/SignUp';
import FeaturedMeals from './components/FeaturedMeals/FeaturedMeals';
import PurchaseRequiredModal from './components/PurchaseRequiredModal/PurchaseRequiredModal';
import SellFoodModal from './components/SellFoodModal/SellFoodModal';
import DeliveryPartnerModal from './components/DeliveryPartnerModal/DeliveryPartnerModal';
import CommunityPlatformPolicies from './components/CommunityPlatformPolicies/CommunityPlatformPolicies';
import PrivacyPolicy from './components/PrivacyPolicy/PrivacyPolicy';
import TermsOfService from './components/TermsOfService/TermsOfService';
import AboutUs from './components/About Us/AboutUs';
import FAQ from './components/FAQ/FAQ';
import CookiePolicy from './components/CookiePolicy/CookiePolicy';
import UserProfile from './components/UserProfile/UserProfile';
import PaymentMethods from './components/PaymentMethods/PaymentMethods';
import LandingPage from './components/LandingPage/LandingPage';
import PaymentSuccess from './components/PaymentSuccess/PaymentSuccess';
import PaymentCancelled from './components/PaymentCancelled/PaymentCancelled';
import { trackPageView, trackAuth, trackSubscription, trackError } from './components/Utils/analytics';
import AdminLogin from './components/Admin/AdminLogin';
import AdminDashboard from './components/Admin/AdminDashboard';

// Initialize Stripe - Replace with your actual Stripe publishable key
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_live_51RYBE6G1JuNgEVmoXJUXLD3o4BSgAs1FUCKhyMGLFE7PrpQAL7qqP74DQAsOBeNy6Equln8dliZYA7NqJQcTs9xT00HUYf8H8F');

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

// Component to track page views
const PageTracker: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(document.title, window.location.href);
  }, [location]);

  return null;
};

function App() {
  const location = useLocation();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPaymentConfirmation, setShowPaymentConfirmation] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [hasPurchasedPlan, setHasPurchasedPlan] = useState(false);
  const [showPurchaseRequired, setShowPurchaseRequired] = useState(false);
  const [showSellFoodModal, setShowSellFoodModal] = useState(false);
  const [showDeliveryPartnerModal, setShowDeliveryPartnerModal] = useState(false);
  const [isAuthChecked, setIsAuthChecked] = useState(false);
  const [isSubscriptionChecked, setIsSubscriptionChecked] = useState(false);

  // Check if we're on admin routes
  const isAdminRoute = location.pathname.startsWith('/admin');

  // Check authentication status on app load - SIMPLIFIED
  React.useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const authToken = localStorage.getItem('authToken');
        const userEmail = localStorage.getItem('userEmail');
        
        // Simple check - if we have auth data, user is logged in
        if (authToken && userEmail) {
          setIsLoggedIn(true);
        } else {
          setIsLoggedIn(false);
        }
      } catch (error) {
        console.error('Error checking auth status:', error);
        trackError('auth_check_failed', 'app_init');
        // Even on error, check if we have auth data
        const authToken = localStorage.getItem('authToken');
        const userEmail = localStorage.getItem('userEmail');
        if (authToken && userEmail) {
          setIsLoggedIn(true);
        }
      } finally {
        setIsAuthChecked(true);
      }
    };

    checkAuthStatus();
  }, []);

  // Separate effect for subscription checking - only runs when user is logged in
  React.useEffect(() => {
    if (!isLoggedIn || !isAuthChecked) {
      setIsSubscriptionChecked(true); // Mark as checked if not logged in
      return;
    }

    const checkSubscriptionWithRetry = async (attempt = 1, maxAttempts = 3) => {
      try {
        console.log(`🔍 Checking subscription status (attempt ${attempt}/${maxAttempts})`);
        
        const authToken = localStorage.getItem('authToken');
        if (!authToken) {
          console.log('❌ No auth token found for subscription check');
          setIsSubscriptionChecked(true);
          return;
        }

        const subscriptionResponse = await fetch('https://api.homecookt.com/api/v1/paypal/current-subscription', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json',
          },
        });

        console.log(`📡 Subscription check response status: ${subscriptionResponse.status}`);

        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          console.log('📊 Subscription data:', subscriptionData);
          
          // Check for both COMPLETED and ACTIVE statuses
          if (subscriptionData.status === 'COMPLETED' || subscriptionData.status === 'ACTIVE') {
            console.log('✅ Active subscription found');
            setHasPurchasedPlan(true);
            trackSubscription('subscription_activated', 'existing');
          } else {
            console.log('❌ No active subscription found, status:', subscriptionData.status);
            setHasPurchasedPlan(false);
          }
        } else if (subscriptionResponse.status === 404) {
          console.log('❌ No subscription found (404)');
          setHasPurchasedPlan(false);
        } else if (attempt < maxAttempts) {
          console.log(`⏳ Subscription check failed, retrying in ${attempt}s...`);
          setTimeout(() => {
            checkSubscriptionWithRetry(attempt + 1, maxAttempts);
          }, attempt * 1000);
          return; // Don't mark as checked yet
        } else {
          console.log('❌ Subscription check failed after all retries');
          setHasPurchasedPlan(false);
          trackError('subscription_check_failed_final', 'app_subscription_check');
        }
      } catch (error) {
        console.error('🚨 Subscription check network error:', error);
        
        if (attempt < maxAttempts) {
          console.log(`⏳ Network error, retrying in ${attempt}s...`);
          setTimeout(() => {
            checkSubscriptionWithRetry(attempt + 1, maxAttempts);
          }, attempt * 1000);
          return; // Don't mark as checked yet
        } else {
          console.log('❌ Subscription check failed after all retries due to network error');
          setHasPurchasedPlan(false);
          trackError('subscription_check_network_error_final', 'app_subscription_check');
        }
      } finally {
        // Only mark as checked if this is the final attempt or success
        if (attempt >= maxAttempts) {
          setIsSubscriptionChecked(true);
        }
      }
    };

    // Add a small delay to ensure auth is fully settled
    const timer = setTimeout(() => {
      checkSubscriptionWithRetry();
    }, 500);

    return () => clearTimeout(timer);
  }, [isLoggedIn, isAuthChecked]);

  // Listen for manual subscription refresh events
  React.useEffect(() => {
    const handleRefreshSubscription = () => {
      console.log('🔄 Manual subscription refresh triggered');
      setIsSubscriptionChecked(false);
      setHasPurchasedPlan(false);
      
      // Trigger subscription check again
      setTimeout(() => {
        if (isLoggedIn) {
          const authToken = localStorage.getItem('authToken');
          if (authToken) {
            fetch('https://api.homecookt.com/api/v1/paypal/current-subscription', {
              method: 'GET',
              headers: {
                'Authorization': `Bearer ${authToken}`,
                'Content-Type': 'application/json',
              },
            })
            .then(response => response.json())
            .then(data => {
              if (data.status === 'COMPLETED' || data.status === 'ACTIVE') {
                setHasPurchasedPlan(true);
              }
              setIsSubscriptionChecked(true);
            })
            .catch(() => {
              setIsSubscriptionChecked(true);
            });
          }
        }
      }, 100);
    };

    window.addEventListener('refreshSubscription', handleRefreshSubscription);
    return () => window.removeEventListener('refreshSubscription', handleRefreshSubscription);
  }, [isLoggedIn]);

  // Listen for payment success events to update subscription status
  React.useEffect(() => {
    const handlePaymentSuccess = () => {
      setHasPurchasedPlan(true);
      setShowPaymentModal(false);
      trackSubscription('payment_completed', selectedPlan || 'unknown');
    };

    window.addEventListener('paymentSuccess', handlePaymentSuccess);
    return () => window.removeEventListener('paymentSuccess', handlePaymentSuccess);
  }, [selectedPlan]);

  // Prevent navigation to auth pages when logged in
  React.useEffect(() => {
    if (isLoggedIn && isAuthChecked) {
      const handlePopState = (event: PopStateEvent) => {
        const currentPath = window.location.pathname;
        if (currentPath === '/login' || currentPath === '/signup') {
          // If user tries to go back to auth pages, redirect to home
          window.history.replaceState(null, '', '/');
          window.location.reload();
        }
      };

      window.addEventListener('popstate', handlePopState);
      return () => window.removeEventListener('popstate', handlePopState);
    }
  }, [isLoggedIn, isAuthChecked]);

  const handlePlanSelect = (plan: string) => {
    // Prevent plan selection if user already has an active subscription
    if (hasPurchasedPlan) {
      alert('You already have an active subscription. Please contact support to change your plan.');
      trackError('duplicate_subscription_attempt', 'subscription_plans');
      return;
    }
    setSelectedPlan(plan);
    trackSubscription('plan_selected', plan);
  };

  const handleContinue = () => {
    // Prevent payment modal if user already has an active subscription
    if (hasPurchasedPlan) {
      alert('You already have an active subscription. Please contact support to change your plan.');
      trackError('duplicate_payment_attempt', 'subscription_plans');
      return;
    }
    setShowPaymentModal(true);
    trackSubscription('payment_started', selectedPlan || 'unknown');
  };

  const handlePaymentSuccess = () => {
    setShowPaymentModal(false);
    setShowPaymentConfirmation(true);
    setHasPurchasedPlan(true);
    trackSubscription('payment_completed', selectedPlan || 'unknown');
  };

  const handleClosePayment = () => {
    setShowPaymentModal(false);
  };

  const handleCloseConfirmation = () => {
    setShowPaymentConfirmation(false);
    setSelectedPlan(null);
  };

  const handleShowTerms = () => {
    setShowTerms(true);
  };

  const handleCloseTerms = () => {
    setShowTerms(false);
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    trackAuth('login');
  };

  const handleLogout = () => {
    // ONLY logout when user explicitly clicks "Sign Out"
    setIsLoggedIn(false);
    setHasPurchasedPlan(false);
    
    // Clear ALL authentication and order data
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userid');
    localStorage.removeItem('tokenType');
    localStorage.removeItem('currentOrderReference');
    localStorage.removeItem('currentOrderPlan');
    localStorage.removeItem('currentOrderAmount');
    localStorage.removeItem('currentOrderCurrency');
    localStorage.removeItem('currentOrderId');
    localStorage.removeItem('currentChargedAmount');
    localStorage.removeItem('currentChargedCurrency');
    localStorage.removeItem('currentOrderStatus');
    localStorage.removeItem('currentUserId');
    
    // Clear browser history and redirect to home
    window.history.replaceState(null, '', '/');
    
    trackAuth('logout');
  };

  const handleClosePurchaseRequired = () => {
    setShowPurchaseRequired(false);
  };

  const handleShowSellFood = () => {
    if (!hasPurchasedPlan) {
      setShowPurchaseRequired(true);
      return;
    }
    setShowSellFoodModal(true);
  };

  const handleCloseSellFood = () => {
    setShowSellFoodModal(false);
  };

  const handleShowDeliveryPartner = () => {
    if (!hasPurchasedPlan) {
      setShowPurchaseRequired(true);
      return;
    }
    setShowDeliveryPartnerModal(true);
  };

  const handleCloseDeliveryPartner = () => {
    setShowDeliveryPartnerModal(false);
  };

  const handleShowSubscriptionFromSellFood = () => {
    setShowSellFoodModal(false);
    // Scroll to subscription section
    setTimeout(() => {
      const subscriptionSection = document.querySelector('#subscription-section');
      if (subscriptionSection) {
        subscriptionSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handleShowSubscriptionFromDelivery = () => {
    setShowDeliveryPartnerModal(false);
    // Scroll to subscription section
    setTimeout(() => {
      const subscriptionSection = document.querySelector('#subscription-section');
      if (subscriptionSection) {
        subscriptionSection.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  // Show loading state while checking authentication
  if (!isAuthChecked || (isLoggedIn && !isSubscriptionChecked)) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e9ecef',
          borderTop: '4px solid var(--primary-color)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p>{!isAuthChecked ? 'Loading...' : 'Checking subscription status...'}</p>
      </div>
    );
  }

  return (
    <AppContainer>
      <PageTracker />
      {!isAdminRoute && (
        <Header 
          isLoggedIn={isLoggedIn} 
          onLogout={handleLogout}
        />
      )}

      <MainContent>
        <Routes>
          {/* Auth Routes - Redirect to home if already logged in */}
          <Route path="/login" element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <Login onLogin={handleLogin} />
            )
          } />
          <Route path="/signup" element={
            isLoggedIn ? (
              <Navigate to="/" replace />
            ) : (
              <SignUp />
            )
          } />
          
          {/* Protected User Routes - Require authentication */}
          <Route path="/profile" element={
            isLoggedIn ? (
              <UserProfile onLogout={handleLogout} />
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          
          {/* Payment Methods - Block if user has active subscription */}
          <Route path="/payment-methods" element={
            !isLoggedIn ? (
              <Navigate to="/login" replace />
            ) : hasPurchasedPlan ? (
              <Navigate to="/" replace />
            ) : (
              <PaymentMethods />
            )
          } />
          
          {/* Payment Pages - Allow access with basic auth check */}
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancelled" element={<PaymentCancelled />} />
          
          {/* Public Routes */}
          <Route path="/" element={
            !isLoggedIn ? (
              <LandingPage />
            ) : (
              <Navigate to="/home" replace />
            )
          } />
          
          {/* Protected Home Route - Only accessible after login */}
          <Route path="/home" element={
            isLoggedIn ? (
              <>
                <HeroSection />
                <FeaturedMeals />
                <EarnMoneySection 
                  onSellFood={handleShowSellFood}
                  onDeliveryPartner={handleShowDeliveryPartner}
                  hasPurchasedPlan={hasPurchasedPlan}
                />
                <SubscriptionPlans 
                  selectedPlan={selectedPlan} 
                  onSelectPlan={handlePlanSelect} 
                  onContinue={handleContinue}
                  onShowTerms={handleShowTerms}
                  hasPurchasedPlan={hasPurchasedPlan}
                />
              </>
            ) : (
              <Navigate to="/login" replace />
            )
          } />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/community-platform-policies" element={<CommunityPlatformPolicies />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/cookie-policy" element={<CookiePolicy />} />
          
          {/* Admin Route */}
          <Route path="/admin" element={
            <AdminLogin />
          } />
          <Route path="/admin/dashboard" element={
            <AdminDashboard />
          } />
        </Routes>
      </MainContent>

      <Footer />

      {/* Only show payment modal if user doesn't have active subscription */}
      {showPaymentModal && !hasPurchasedPlan && (
        <PaymentModal 
          onClose={handleClosePayment}
          onSuccess={handlePaymentSuccess}
          selectedPlan={selectedPlan || 'monthly'}
          stripePromise={stripePromise}
        />
      )}

      {showPaymentConfirmation && (
        <PaymentConfirmation onClose={handleCloseConfirmation} />
      )}

      {showTerms && (
        <TermsConditions onClose={handleCloseTerms} />
      )}

      {showPurchaseRequired && (
        <PurchaseRequiredModal onClose={handleClosePurchaseRequired} />
      )}

      {showSellFoodModal && (
        <SellFoodModal 
          onClose={handleCloseSellFood} 
          hasPurchasedPlan={hasPurchasedPlan}
          onShowSubscription={handleShowSubscriptionFromSellFood}
        />
      )}

      {showDeliveryPartnerModal && (
        <DeliveryPartnerModal 
          onClose={handleCloseDeliveryPartner}
          hasPurchasedPlan={hasPurchasedPlan}
          onShowSubscription={handleShowSubscriptionFromDelivery}
        />
      )}
    </AppContainer>
  );
}

export default App;